import React, { useEffect, useState } from 'react';
import { api, auth } from './auth';

const VERDICT_COLORS = {
  COMPLIANT: { bg: '#F0FDF4', color: '#16A34A', border: '#BBF7D0' },
  'NON-COMPLIANT': { bg: '#FEF2F2', color: '#DC2626', border: '#FECACA' },
  INCOMPLETE: { bg: '#FFFBEB', color: '#D97706', border: '#FDE68A' },
  ERROR: { bg: '#F3F4F6', color: '#6B7280', border: '#E5E7EB' },
};

export default function History({ user, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupBatches, setGroupBatches] = useState(true);

  useEffect(() => {
    api.get('/history').then(r => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const download = async (filename) => {
    try { await auth.downloadAuthenticated(`/report/${filename}`, filename); }
    catch (err) { alert('Download failed: ' + (err.response?.data?.detail || err.message)); }
  };

  // Group consecutive batch members for visual grouping
  const grouped = [];
  if (groupBatches) {
    const seen = new Set();
    for (const item of items) {
      if (item.batch_size > 1 && item.batch_id && !seen.has(item.batch_id)) {
        const batchItems = items.filter(i => i.batch_id === item.batch_id);
        grouped.push({ type: 'batch', batch_id: item.batch_id, items: batchItems });
        seen.add(item.batch_id);
      } else if (!item.batch_id || item.batch_size <= 1) {
        grouped.push({ type: 'single', item });
      }
    }
  }

  return (
    <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '20px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>Verification history</h2>
          <p style={{ fontSize: 12, color: '#6B7280', margin: '4px 0 0 0' }}>
            {user?.role === 'super_admin' ? 'All verifications across the platform' : user?.role === 'company_admin' ? 'All verifications in your company' : 'Your verifications'} — last 50
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6B7280', cursor: 'pointer' }}>
            <input type="checkbox" checked={groupBatches} onChange={e => setGroupBatches(e.target.checked)} />
            Group batches
          </label>
          {onClose && <button onClick={onClose} style={{ background: 'transparent', color: '#6B7280', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>Back</button>}
        </div>
      </div>

      {loading ? <p style={{ color: '#9CA3AF', fontSize: 13 }}>Loading…</p>
       : items.length === 0 ? <p style={{ color: '#9CA3AF', fontSize: 13 }}>No verifications yet.</p>
       : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: '#F8F9FB', textAlign: 'left' }}>
              <th style={th}>Date</th><th style={th}>File / Mandate</th><th style={th}>Source</th>
              <th style={th}>Verdict</th><th style={th}>Score</th><th style={th}>Fields</th>
              {user?.role !== 'verifier' && <th style={th}>User</th>}
              {user?.role === 'super_admin' && <th style={th}>Company</th>}
              <th style={th}>Report</th>
            </tr></thead>
            <tbody>
              {(groupBatches ? grouped : items.map(i => ({ type: 'single', item: i }))).map((row, idx) => {
                if (row.type === 'batch') {
                  const first = row.items[0];
                  const compliantCount = row.items.filter(i => i.verdict === 'COMPLIANT').length;
                  return (
                    <React.Fragment key={row.batch_id}>
                      <tr style={{ background: '#F8F9FB', borderTop: '2px solid #E5E7EB' }}>
                        <td colSpan={user?.role === 'super_admin' ? 9 : (user?.role !== 'verifier' ? 8 : 7)} style={{ padding: '8px 12px' }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' }}>
                            📦 Batch · {first.filename} · {row.items.length} mandates · {compliantCount} compliant
                          </span>
                        </td>
                      </tr>
                      {row.items.map(it => <Row key={it.id} it={it} user={user} download={download} indent />)}
                    </React.Fragment>
                  );
                }
                return <Row key={row.item.id} it={row.item} user={user} download={download} />;
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Row({ it, user, download, indent }) {
  const c = VERDICT_COLORS[it.verdict] || VERDICT_COLORS.ERROR;
  return <tr style={{ borderBottom: '0.5px solid #F3F4F6', background: indent ? '#FAFBFC' : 'transparent' }}>
    <td style={td}>{new Date(it.created_at).toLocaleString()}</td>
    <td style={td}>
      {indent && <span style={{ color: '#9CA3AF', marginRight: 6 }}>↳</span>}
      <span>{it.mandate_identifier || it.filename}</span>
      {it.batch_size > 1 && (
        <span style={{ marginLeft: 6, fontSize: 10, color: '#6B7280', background: '#EFF6FF', padding: '1px 6px', borderRadius: 3, fontWeight: 600 }}>
          {it.batch_position}/{it.batch_size}
        </span>
      )}
    </td>
    <td style={td}><span style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase' }}>{it.source}</span></td>
    <td style={td}>
      <span style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}`, borderRadius: 6, padding: '2px 8px', fontWeight: 600, fontSize: 11 }}>{it.verdict}</span>
      {it.requires_review && !it.reviewed_at && <span style={{ marginLeft: 6, background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A', borderRadius: 6, padding: '2px 7px', fontWeight: 600, fontSize: 10 }}>review</span>}
      {it.reviewed_at && <span style={{ marginLeft: 6, color: '#15803D', fontSize: 10, fontWeight: 600 }}>reviewed</span>}
    </td>
    <td style={td}>{it.compliance_score}/100</td>
    <td style={td}>{it.fields_present}/{it.fields_total}</td>
    {user?.role !== 'verifier' && <td style={{ ...td, color: '#6B7280' }}>{it.user_email}</td>}
    {user?.role === 'super_admin' && <td style={{ ...td, color: '#6B7280' }}>{it.company_name || '—'}</td>}
    <td style={td}>
      {it.report_filename && !it.report_expired
        ? <button onClick={() => download(it.report_filename)} style={{ background: 'transparent', color: '#2563EB', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500, padding: 0 }}>↓ Download</button>
        : <span style={{ color: '#9CA3AF', fontSize: 12 }}>Expired</span>}
    </td>
  </tr>;
}

const th = { padding: '10px 12px', fontWeight: 500, fontSize: 11, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px solid #E5E7EB' };
const td = { padding: '10px 12px', color: '#111827' };
